import React, { useState, useEffect } from "react";
import axios from "../../utils/api";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Grid,
  Box,
  Paper,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

const UsersDetailsList = () => {
  const { email } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedUser, setUpdatedUser] = useState({});
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [expandedLocations, setExpandedLocations] = useState({});
  const navigate = useNavigate();

  const toggleLocationExpand = (index) => {
    setExpandedLocations((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // When dialog opens, expand first location only for a cleaner view
  useEffect(() => {
    if (open && updatedUser.locations?.length) {
      setExpandedLocations({ 0: true });
    }
  }, [open, updatedUser.locations?.length]);

  const decodeEmail = (encodedEmail) => {
    return atob(decodeURIComponent(encodedEmail));
  };

  const FieldLabel = ({ icon: IconCmp, children }) => (
    <Typography
      variant="subtitle2"
      sx={{
        color: "#718096",
        fontWeight: "700",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <IconCmp sx={{ fontSize: 18, color: "#64748b" }} />
      {children}
    </Typography>
  );

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a5b4fc" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6366f1",
        borderWidth: 2,
      },
    },
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const decodedEmail = decodeEmail(email);
        const token = localStorage.getItem("token");
        const response = await axios.get(`/get-user-details/${decodedEmail}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(response.data);
        setUpdatedUser({
          ...response.data,
          primaryContact: response.data.primaryContact || {
            name: "",
            email: "",
            phoneNumber: "",
          },
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching user:", err);
        setError("Failed to fetch user");
        setLoading(false);
      }
    };

    fetchUser();
  }, [email]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUpdatedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrimaryContactChange = (event) => {
    const { name, value } = event.target;
    setUpdatedUser((prev) => ({
      ...prev,
      primaryContact: {
        ...prev.primaryContact,
        [name]: value,
      },
    }));
  };

  const handleLocationChange = (index, event) => {
    const { name, value } = event.target;
    const newLocations = [...updatedUser.locations];
    newLocations[index] = {
      ...newLocations[index],
      [name]: value,
    };
    setUpdatedUser((prev) => ({
      ...prev,
      locations: newLocations,
    }));
  };
  const handleAddressChange = (locationIndex, addressKey, event) => {
    const { value } = event.target;
    const newLocations = [...updatedUser.locations];
    newLocations[locationIndex].address[addressKey] = value;
    setUpdatedUser((prev) => ({
      ...prev,
      locations: newLocations,
    }));
  };
  const handleSubLocationChange = (locationIndex, subLocationIndex, event) => {
    const { value } = event.target;
    const newLocations = [...updatedUser.locations];
    newLocations[locationIndex].subLocations[subLocationIndex] = {
      ...newLocations[locationIndex].subLocations[subLocationIndex],
      name: value,
    };
    setUpdatedUser((prev) => ({
      ...prev,
      locations: newLocations,
    }));
  };

  const handleAddLocation = () => {
    setUpdatedUser((prev) => ({
      ...prev,
      locations: [
        ...prev.locations,
        {
          name: "",
          address: { street: "", town: "", city: "", state: "", pinCode: "" },
          subLocations: [{ name: "" }],
        },
      ],
    }));
  };

  const handleRemoveLocation = (locationIndex) => {
    if (locationIndex === 0) return; // First location cannot be deleted
    const newLocations = updatedUser.locations.filter((_, i) => i !== locationIndex);
    setUpdatedUser((prev) => ({ ...prev, locations: newLocations }));
    setExpandedLocations((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, val]) => {
        const k = Number(key);
        if (k < locationIndex) next[k] = val;
        if (k > locationIndex) next[k - 1] = val;
      });
      return next;
    });
  };

  const handleAddSubLocation = (locationIndex) => {
    const newLocations = [...updatedUser.locations];
    newLocations[locationIndex].subLocations.push({ name: "" });
    setUpdatedUser((prev) => ({
      ...prev,
      locations: newLocations,
    }));
  };

  const handleRemoveSubLocation = (locationIndex, subLocationIndex) => {
    if (subLocationIndex === 0) {
      return;
    }
    const newLocations = [...updatedUser.locations];
    newLocations[locationIndex].subLocations.splice(subLocationIndex, 1);
    setUpdatedUser((prev) => ({
      ...prev,
      locations: newLocations,
    }));
  };

  const handleSubmit = async () => {
    // Basic Validation
    if (!updatedUser.name || !updatedUser.email || !updatedUser.phoneNumber) {
      setSnackbarMessage("Please fill all required fields.");
      setSnackbarOpen(true);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(updatedUser.email)) {
      setSnackbarMessage("Please enter a valid email address.");
      setSnackbarOpen(true);
      return;
    }

    // Phone number validation (basic example: check if it's 10 digits)
    const phoneNumberRegex = /^\d{10}$/;
    if (
      updatedUser.phoneNumber &&
      !phoneNumberRegex.test(updatedUser.phoneNumber)
    ) {
      setSnackbarMessage("Please enter a valid phone number.");
      setSnackbarOpen(true);
      return;
    }
    // Validation for Primary Contact
    if (!updatedUser.primaryContact.name) {
      setSnackbarMessage("Please enter a primary contact name.");
      setSnackbarOpen(true);
      return;
    }

    if (!updatedUser.primaryContact.email) {
      setSnackbarMessage("Please enter a primary contact email.");
      setSnackbarOpen(true);
      return;
    }
    if (!emailRegex.test(updatedUser.primaryContact.email)) {
      setSnackbarMessage(
        "Please enter a valid email address for primary contact."
      );
      setSnackbarOpen(true);
      return;
    }

    if (
      updatedUser.primaryContact.phoneNumber &&
      !phoneNumberRegex.test(updatedUser.primaryContact.phoneNumber)
    ) {
      setSnackbarMessage(
        "Please enter a valid phone number for primary contact."
      );
      setSnackbarOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`/putuser/${user._id}`, updatedUser, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setOpen(false);
      setSnackbarMessage("User details updated successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbarMessage("Failed to update user details");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Updated loading state UI
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          overflow: "hidden",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        }}
      >
        <div
          className="wrapper"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            margin: 0,
            inset: "auto",
            width: "max-content",
            zIndex: 1300,
          }}
        >
          <div className="circle"></div>
          <div className="circle"></div>
          <div className="circle"></div>
          <div className="shadow"></div>
          <div className="shadow"></div>
          <div className="shadow"></div>
          <span>Loading</span>
        </div>
      </div>
    );
  }

  if (error)
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          p: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 3,
            borderColor: "divider",
            textAlign: "center",
            maxWidth: 520,
            width: "100%",
            backgroundColor: "rgba(255,255,255,0.92)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: "error.main" }}>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please try again.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Paper>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 } }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  border: "1px solid",
                  borderColor: "divider",
                  backdropFilter: "blur(10px)",
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: -0.5 }}>
                  {user?.name || "User"}'s Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and update account details.
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={handleOpen}
              variant="contained"
              size="large"
              sx={{
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                borderRadius: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 10px 24px rgba(102, 126, 234, 0.28)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6eea 0%, #6f44a0 100%)",
                  boxShadow: "0 14px 30px rgba(102, 126, 234, 0.34)",
                },
              }}
            >
              Update User Details
            </Button>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 3,
              borderColor: "divider",
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Overview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Basic info, permissions, contacts, and locations.
              </Typography>
            </Box>
            <Divider />

            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Grid
                container
                rowSpacing={{ xs: 4, md: 10 }}
                columnSpacing={{ xs: 3, md: 5 }}
              >
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "#f8f9ff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.03)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600",
                    marginBottom: "20px",
                    color: "#4a5568",
                    display: "flex",
                    alignItems: "center",
                    "&:after": {
                      content: '""',
                      flex: 1,
                      height: "1px",
                      background: "#e2e8f0",
                      marginLeft: "15px",
                    },
                  }}
                >
                  Basic Information
                </Typography>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={EmailOutlinedIcon}>Email</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.email}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={PhoneOutlinedIcon}>Phone Number</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.phoneNumber}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={BusinessOutlinedIcon}>Organization</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.organizationName || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={ShoppingCartOutlinedIcon}>Purchase Type</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.purchaseType || "Not specified"}
                  </Typography>
                </Box>

                <Box>
                  <FieldLabel icon={WorkspacePremiumOutlinedIcon}>Subscription Plan</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.subscriptionPlan || "Not specified"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "#f8f9ff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.03)",
                  height: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600",
                    marginBottom: "20px",
                    color: "#4a5568",
                    display: "flex",
                    alignItems: "center",
                    "&:after": {
                      content: '""',
                      flex: 1,
                      height: "1px",
                      background: "#e2e8f0",
                      marginLeft: "15px",
                    },
                  }}
                >
                  App Permissions
                </Typography>

                <Box sx={{ marginBottom: "25px" }}>
                  <FieldLabel icon={MapOutlinedIcon}>Manual Mapping</FieldLabel>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "500",
                      color: user.manualMapping ? "#10b981" : "#ef4444",
                    }}
                  >
                    {user.manualMapping}
                  </Typography>
                </Box>

                 <Box sx={{ marginBottom: "25px" }}>
                  <FieldLabel icon={ScienceOutlinedIcon}>Object Disinfection</FieldLabel>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "500",
                      color: user.objectDisinfection ? "#10b981" : "#ef4444",
                    }}
                  >
                    {user.objectDisinfection}
                  </Typography>
                </Box>

                 <Box>
                  <FieldLabel icon={VerifiedOutlinedIcon}>Subscription Status</FieldLabel>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "500",
                      color: user.subscriptionStatus ? "#10b981" : "#ef4444",
                    }}
                  >
                    {user.subscriptionStatus}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "#f8f9ff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.03)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600",
                    marginBottom: "20px",
                    color: "#4a5568",
                    display: "flex",
                    alignItems: "center",
                    "&:after": {
                      content: '""',
                      flex: 1,
                      height: "1px",
                      background: "#e2e8f0",
                      marginLeft: "15px",
                    },
                  }}
                >
                  Primary Contact
                </Typography>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={PersonOutlineOutlinedIcon}>Name</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.primaryContact?.name || "N/A"}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: "20px" }}>
                  <FieldLabel icon={EmailOutlinedIcon}>Email</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.primaryContact?.email || "N/A"}
                  </Typography>
                </Box>

                <Box>
                  <FieldLabel icon={PhoneOutlinedIcon}>Phone Number</FieldLabel>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {user.primaryContact?.phoneNumber || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: "#f8f9ff",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.03)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600",
                    marginBottom: "20px",
                    color: "#4a5568",
                    display: "flex",
                    alignItems: "center",
                    "&:after": {
                      content: '""',
                      flex: 1,
                      height: "1px",
                      background: "#e2e8f0",
                      marginLeft: "15px",
                    },
                  }}
                >
                  Locations
                </Typography>

                {user.locations.map((location, index) => (
                  <Box
                    key={index}
                    sx={{
                      marginBottom: "25px",
                      padding: "16px",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "600", marginBottom: "12px" }}
                    >
                      {location.name}
                    </Typography>

                    <Typography variant="body2" sx={{ marginBottom: "8px" }}>
                      {location.address.street}, {location.address.town}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: "8px" }}>
                      {location.address.city}, {location.address.state} -{" "}
                      {location.address.pinCode}
                    </Typography>

                    {location.subLocations.length > 0 && (
                      <Box sx={{ marginTop: "12px" }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "600", color: "#4a5568" }}
                        >
                          Sub-Locations:
                        </Typography>
                        <ul
                          style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}
                        >
                          {location.subLocations.map((sub, subIndex) => (
                            <li key={subIndex}>
                              <Typography variant="body2">
                                {sub.name}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
              </Grid>
            </Box>

          </Paper>
        </Stack>

      {/* Update User Details Dialog - Redesigned */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(99, 102, 241, 0.15), 0 8px 24px rgba(0,0,0,0.08)",
            maxHeight: "92vh",
          },
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
            color: "#fff",
            px: 3,
            py: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" },
            }}
            size="medium"
          >
            <CloseIcon />
          </IconButton>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <DialogTitle sx={{ color: "inherit", fontWeight: 700, fontSize: "1.5rem", p: 0, m: 0 }}>
                Update User Details
              </DialogTitle>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
                Edit profile, contact and location information
              </Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent
          sx={{
            p: 0,
            bgcolor: "#f8fafc",
            overflowY: "auto",
          }}
        >
          <Box sx={{ p: 3 }}>
            {/* User details card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#334155",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BadgeOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                User information
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User Name"
                    name="name"
                    value={updatedUser.name || ""}
                    onChange={handleChange}
                    placeholder="Full name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    name="email"
                    type="email"
                    value={updatedUser.email || ""}
                    onChange={handleChange}
                    placeholder="user@example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone Number"
                    name="phoneNumber"
                    value={updatedUser.phoneNumber || ""}
                    onChange={handleChange}
                    placeholder="10 digits"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Primary contact card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: "#334155",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <PersonOutlineOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                Primary contact
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Name"
                    name="name"
                    value={updatedUser.primaryContact?.name || ""}
                    onChange={handlePrimaryContactChange}
                    placeholder="Contact name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Email"
                    name="email"
                    type="email"
                    value={updatedUser.primaryContact?.email || ""}
                    onChange={handlePrimaryContactChange}
                    placeholder="contact@example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Contact Phone"
                    name="phoneNumber"
                    value={updatedUser.primaryContact?.phoneNumber || ""}
                    onChange={handlePrimaryContactChange}
                    placeholder="10 digits"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Locations section */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#334155",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PlaceOutlinedIcon sx={{ fontSize: 22, color: "#6366f1" }} />
                  Locations
                </Typography>
                <Button
                  onClick={handleAddLocation}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#6366f1",
                    borderColor: "#c7d2fe",
                    "&:hover": { borderColor: "#6366f1", bgcolor: "rgba(99, 102, 241, 0.06)" },
                  }}
                >
                  Add location
                </Button>
              </Stack>

              {updatedUser.locations &&
                updatedUser.locations.map((location, locationIndex) => (
                  <Paper
                    key={locationIndex}
                    variant="outlined"
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      overflow: "hidden",
                      borderColor: "divider",
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Box
                      onClick={() => toggleLocationExpand(locationIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        bgcolor: "rgba(99, 102, 241, 0.04)",
                        "&:hover": { bgcolor: "rgba(99, 102, 241, 0.08)" },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocationOnOutlinedIcon sx={{ color: "#6366f1", fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                          {location.name || `Location ${locationIndex + 1}`}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {locationIndex > 0 && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLocation(locationIndex);
                            }}
                            sx={{
                              color: "#ef4444",
                              bgcolor: "#fef2f2",
                              "&:hover": { bgcolor: "#fee2e2", color: "#dc2626" },
                            }}
                            title="Delete location"
                          >
                            <FaTrash fontSize={14} />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLocationExpand(locationIndex);
                          }}
                          aria-label={expandedLocations[locationIndex] ? "Collapse" : "Expand"}
                        >
                          {expandedLocations[locationIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Stack>
                    </Box>
                    <Collapse in={expandedLocations[locationIndex] === true}>
                      <Box sx={{ p: 2, pt: 0 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Location Name"
                              name="name"
                              value={location.name || ""}
                              onChange={(e) => handleLocationChange(locationIndex, e)}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Street"
                              value={location.address?.street || ""}
                              onChange={(e) => handleAddressChange(locationIndex, "street", e)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <HomeOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Town"
                              value={location.address?.town || ""}
                              onChange={(e) => handleAddressChange(locationIndex, "town", e)}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="City"
                              value={location.address?.city || ""}
                              onChange={(e) => handleAddressChange(locationIndex, "city", e)}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="State"
                              value={location.address?.state || ""}
                              onChange={(e) => handleAddressChange(locationIndex, "state", e)}
                              sx={textFieldSx}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Pin Code"
                              value={location.address?.pinCode || ""}
                              onChange={(e) => handleAddressChange(locationIndex, "pinCode", e)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PinDropOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={textFieldSx}
                            />
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 1 }}>
                            Sub-locations
                          </Typography>
                          <Stack spacing={1}>
                            {location.subLocations?.map((subLocation, subLocationIndex) => (
                              <Stack key={subLocationIndex} direction="row" spacing={1} alignItems="center">
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Sub-location name"
                                  value={subLocation.name || ""}
                                  onChange={(e) =>
                                    handleSubLocationChange(locationIndex, subLocationIndex, e)
                                  }
                                  sx={textFieldSx}
                                />
                                {subLocationIndex > 0 && (
                                  <IconButton
                                    onClick={() => handleRemoveSubLocation(locationIndex, subLocationIndex)}
                                    size="small"
                                    sx={{
                                      color: "#ef4444",
                                      bgcolor: "#fef2f2",
                                      "&:hover": { bgcolor: "#fee2e2" },
                                    }}
                                  >
                                    <FaTrash fontSize={14} />
                                  </IconButton>
                                )}
                              </Stack>
                            ))}
                            <Button
                              onClick={() => handleAddSubLocation(locationIndex)}
                              size="small"
                              startIcon={<AddIcon />}
                              sx={{
                                textTransform: "none",
                                color: "#64748b",
                                alignSelf: "flex-start",
                                "&:hover": { bgcolor: "rgba(99, 102, 241, 0.06)", color: "#6366f1" },
                              }}
                            >
                              Add sub-location
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    </Collapse>
                  </Paper>
                ))}
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "#fff",
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#64748b",
              borderColor: "#e2e8f0",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
              },
              "&:disabled": { background: "#cbd5e1", color: "#94a3b8" },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : (
              "Save changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={
            snackbarMessage.includes("successfully") ? "success" : "error"
          }
          sx={{
            width: "100%",
            fontSize: "16px",
            fontWeight: "500",
            borderRadius: "12px",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      </Container>
    </Box>
  );
};

export default UsersDetailsList;
